import Vue from "vue";
import Router from "vue-router";
import VueMeta from "vue-meta";
import Home from "../views/Home";
import UserHome from "../views/UserHome";
import OrderList from "../views/OrderList";
import UserAddress from "../views/UserAddress";
import AccountNewPharmacy from "../views/AccountNewPharmacy";
import RecoverPass from "../views/RecoverPass";
import RecoverPassConfirm from "../views/RecoverPassConfirm";
import RegisterConfirm from "../views/RegisterConfirm";
import AccountNewPatientData from "../views/AccountNewPatientData";
import Login from "../views/Login";
import SocialAuth from "../views/SocialAuth";
import Register from "../views/Register";
import Search from "../views/Search";
import ProductList from "../views/ProductList";
import ProductSheet from "../views/ProductSheet";
import PatientData from "../views/PatientData";
import DoctorList from "../views/DoctorList";
import DoctorData from "../views/DoctorData";
import AddressForm from "../views/AddressForm";
import BrandsList from "../views/BrandsList";
import AccountOrderDetail from "../views/AccountOrderDetail";
import Legal from "../views/Legal";
import TermsAndConditions from "../views/TermsAndConditions";
import Cookies from "../views/Cookies";
import Privacy from "../views/Privacy";
import TermsSale from "../views/TermsSale";
import PharmacyList from "../views/PharmacyList";
import Construction from "../views/Construction";
import NotFound from "../views/NotFound";
import PatientSelection from "../views/PatientSelection";
import FallBackErrorPage from "../views/FallBackErrorPage";
import ShoppingCartReview from "../views/ShoppingCartReview";
import TransactionCheckout from "../views/TransactionCheckout";
import ShoppingCartPaymentDoneIframe from "../views/ShoppingCartPaymentDoneIframe";
import PharmacyDetail from "../views/PharmacyDetail";
import GroupedCategories from "../views/GroupedCategories";
import GroupedCategoriesProducts from "../views/GroupedCategoriesProducts";
//import Services from "../views/Services";
import ServicesTemp from "../views/ServicesTemp";
import Services from "../views/Services";
import ImportMedicinesHome from "../views/ImportMedicinesHome";
import ImportMedicinesForm from "../views/ImportMedicinesForm";
import ImportMedicinesDone from "../views/ImportMedicinesDone";
import Poll from "../views/Poll";
import AboutSite from "../views/AboutSite";
import HelpIndex from "../views/HelpIndex";
import HelpSignUp from "../views/HelpSignUp";
import HelpAccount from "../views/HelpAccount";
import HelpPurchases from "../views/HelpPurchases";
import HelpDelivery from "../views/HelpDelivery";
import HelpPayment from "../views/HelpPayment";
import CustomerService from "../views/CustomerService";
import SiteContact from "../views/SiteContact";
import { store } from "../app";

/*
 * Preventing "NavigationDuplicated" errors in console in Vue-router >= 3.1.0
 * https://github.com/vuejs/vue-router/issues/2881
 * */
["push", "replace"].forEach(method => {
  const originalCall = Router.prototype[method];
  Router.prototype[method] = function(location, onResolve, onReject) {
    if (onResolve || onReject) {
      return originalCall.call(this, location, onResolve, onReject);
    }
    return originalCall.call(this, location).catch(err => {
      // TODO: Capture this in Sentry
      return err;
    });
  };
});

Vue.use(Router);
Vue.use(VueMeta, {
  refreshOnceOnNavigation: true,
});

// scrollBehavior:
// - only available in html5 history mode
// - defaults to no scroll behavior
// - return false to prevent scroll
const scrollBehavior = function(to, from, savedPosition) {
  if (savedPosition) {
    // savedPosition is only available for popstate navigations.
    return savedPosition;
  } else {
    const position = {};

    // scroll to anchor by returning the selector
    if (to.hash) {
      position.selector = to.hash;

      if (process.browser) {
        if (document.querySelector(to.hash)) {
          return position;
        }
      }

      // if the returned position is falsy or an empty object,
      // will retain current scroll position.
      return false;
    }

    return new Promise(resolve => {
      // check if any matched route config has meta that requires scrolling to top
      if (to.matched.some(m => m.meta.scrollToTop)) {
        // coords will be used if no selector is provided,
        // or if the selector didn't match any element.
        position.x = 0;
        position.y = 0;
      }
      resolve(position);
    });
  }
};

export function createRouter() {
  const router = new Router({
    mode: "history",
    routes: [
      {
        path: "/error",
        name: "fallback-error-page",
        component: FallBackErrorPage,
      },
      {
        path: "/",
        name: "home",
        component: Home,
        meta: {
          auth: false,
        },
      },
      {
        path: "/login",
        name: "login",
        component: Login,
        meta: {
          scrollToTop: true,
          auth: false,
          breadcrumb: [
            { langkey: "home.breadcrumb", route: "home" },
            { langkey: "login.breadcrumb" },
          ],
        },
      },
      {
        path: "/register/login",
        name: "register-login",
        component: Login,
        meta: {
          scrollToTop: true,
          auth: false,
          showValidationAlert: true,
          breadcrumb: [
            { langkey: "home.breadcrumb", route: "home" },
            { langkey: "login.breadcrumb" },
          ],
        },
      },
      {
        path: "/login/validated",
        name: "login-validated",
        component: Login,
        meta: {
          scrollToTop: true,
          auth: false,
          showValidatedAlert: true,
          breadcrumb: [
            { langkey: "home.breadcrumb", route: "home" },
            { langkey: "login.breadcrumb" },
          ],
        },
      },
      {
        path: "/login-error",
        name: "login-error",
        component: Login,
        props: route => ({
          errorCode: route.query.code,
        }),
        meta: {
          auth: false,
          error: true,
        },
      },
      {
        path: "/social/auth/done",
        name: "social_auth_done",
        component: SocialAuth,
        meta: {
          auth: false,
          done: true,
        },
      },
      {
        path: "/social/auth/error",
        name: "social_auth_error",
        component: SocialAuth,
        props: route => ({
          code: route.query.code,
        }),
        meta: {
          auth: false,
          error: true,
        },
      },
      {
        path: "/social/auth/:provider",
        name: "social_auth",
        component: SocialAuth,
        meta: {
          auth: false,
        },
      },
      {
        path: "/register",
        name: "register",
        component: Register,
        meta: {
          auth: false,
          breadcrumb: [
            { langkey: "home.breadcrumb", route: "home" },
            { langkey: "register.breadcrumb" },
          ],
        },
      },
      {
        path: "/recover/",
        name: "recover_pass",
        component: RecoverPass,
        meta: {
          auth: false,
          breadcrumb: [
            { langkey: "home.breadcrumb", route: "home" },
            { langkey: "recover_pass.breadcrumb" },
          ],
        },
      },
      {
        path: "/recover/confirm/:uid/:token",
        name: "recover_pass_confirm",
        component: RecoverPassConfirm,
        meta: {
          auth: false,
          breadcrumb: [
            { langkey: "home.breadcrumb", route: "home" },
            { langkey: "recover_pass_confirm.breadcrumb" },
          ],
        },
      },
      {
        path: "/register/confirm/:uid/:token",
        name: "register_confirm",
        component: RegisterConfirm,
        meta: {
          auth: false,
        },
      },
      {
        path: "/patientselector",
        name: "patientselector",
        props: true,
        component: PatientSelection,
        meta: {
          auth: true,
        },
      },
      {
        path: "/site/contact",
        name: "site_contact",
        component: SiteContact,
        meta: {
          auth: false,
        },
      },
      {
        path: "/user/index/:pt_uuid?",
        name: "userhome",
        component: UserHome,
        meta: {
          scrollToTop: true,
          auth: true,
          breadcrumb: [
            { langkey: "home.breadcrumb", route: "home" },
            { langkey: "user_home.breadcrumb" },
          ],
        },
      },
      {
        path: "/user/doctor-information/",
        name: "doctorlist",
        component: DoctorList,
        meta: {
          auth: true,
          breadcrumb: [
            { langkey: "home.breadcrumb", route: "home" },
            {
              langkey: "user_home.breadcrumb",
              route: "userhome",
              params: ["pt_uuid"],
            },
            { langkey: "doctor_list.breadcrumb" },
          ],
        },
      },
      {
        path: "/user/doctor/:doctor_uuid?",
        name: "doctordata",
        component: DoctorData,
        meta: {
          auth: true,
          breadcrumb: [
            { langkey: "home.breadcrumb", route: "home" },
            {
              langkey: "user_home.breadcrumb",
              route: "userhome",
              params: ["pt_uuid"],
            },
            { langkey: "doctor_list.breadcrumb", route: "doctorlist" },
            { langkey: "doctor_information.breadcrumb" },
          ],
        },
      },
      {
        path: "/user/personal/:pt_uuid?",
        name: "patientdata",
        component: PatientData,
        meta: {
          auth: true,
          breadcrumb: [
            { langkey: "home.breadcrumb", route: "home" },
            {
              langkey: "user_home.breadcrumb",
              route: "userhome",
              params: ["pt_uuid"],
            },
            { langkey: "user_information.breadcrumb" },
          ],
        },
      },
      // igual que patientdata, pero redireccionará al carrito cuando la
      // dirección se guarde
      {
        path: "/user/personal/:pt_uuid?",
        name: "patientdata-to-cart",
        component: PatientData,
        meta: {
          auth: true,
          breadcrumb: [
            { langkey: "home.breadcrumb", route: "home" },
            {
              langkey: "user_home.breadcrumb",
              route: "userhome",
              params: ["pt_uuid"],
            },
            { langkey: "user_information.breadcrumb" },
          ],
        },
        props: {
          after_save_redirect: {
            name: "cart_review",
          },
        },
      },
      {
        path: "/user/orders",
        name: "userhistorial",
        component: OrderList,
        meta: {
          scrollToTop: true,
          auth: true,
          breadcrumb: [
            { langkey: "home.breadcrumb", route: "home" },
            { langkey: "user_home.breadcrumb", route: "userhome" },
            { langkey: "user_historial.breadcrumb" },
          ],
        },
      },
      {
        path: "/user/address/:pt_uuid",
        name: "useraddress",
        component: UserAddress,
        meta: {
          auth: true,
          breadcrumb: [
            { langkey: "home.breadcrumb", route: "home" },
            {
              langkey: "user_home.breadcrumb",
              route: "userhome",
              params: ["pt_uuid"],
            },
            { langkey: "user_address.breadcrumb" },
          ],
        },
      },
      // {
      //   path: "/user/programs/:pt_uuid",
      //   name: "userprogram",
      //   component: UserProgram,
      //   meta: {
      //     auth: true,
      //     breadcrumb: [
      //       { langkey: "home.breadcrumb", route: "home" },
      //       { langkey: "user_home.breadcrumb", route: "userhome" },
      //       { langkey: "user_program.breadcrumb" },
      //     ],
      //   },
      // },
      {
        path: "/user/order/:order_uuid",
        name: "order-details",
        component: AccountOrderDetail,
        meta: {
          auth: true,
          breadcrumb: [
            { langkey: "home.breadcrumb", route: "home" },
            { langkey: "user_home.breadcrumb", route: "userhome" },
            { langkey: "user_historial.breadcrumb", route: "userhistorial" },
            { langkey: "order_detail.breadcrumb" },
          ],
        },
      },
      {
        path: "/account/new_pharmacy",
        name: "pharmacy_new",
        meta: { auth: true },
        component: AccountNewPharmacy,
      },
      {
        path: "/account/patientdata_create",
        name: "patientdata_create",
        meta: { auth: true },
        component: AccountNewPatientData,
      },
      {
        path: "/poll",
        name: "poll",
        component: Poll,
        meta: {
          auth: false,
        },
        props: { id: undefined },
      },
      {
        path: "/search",
        name: "search",
        component: Search,
        meta: {
          auth: false,
          breadcrumb: [
            { langkey: "home.breadcrumb", route: "home" },
            { langkey: "results.breadcrumb" },
          ],
        },
        props: { query: false, letter: false },
      },
      {
        path: "/brands",
        name: "listbrand",
        meta: {
          auth: false,
          breadcrumb: [
            { langkey: "home.breadcrumb", route: "home" },
            { langkey: "brands.breadcrumb" },
          ],
        },
        props: route => ({
          letter: route.query.letter,
        }),
        component: BrandsList,
      },
      {
        path: "/what-is-vooymarket",
        name: "what-is-vooymarket",
        component: AboutSite,
        meta: {
          scrollToTop: true,
          auth: false,
          breadcrumb: [
            { langkey: "home.breadcrumb", route: "home" },
            {
              langkey: "about_vooy_market.breadcrumb",
              route: "what-is-vooymarket",
            },
          ],
        },
      },
      {
        path: "/how-does-it-work",
        name: "how-does-it-work",
        component: HelpIndex,
        meta: {
          scrollToTop: true,
          auth: false,
          breadcrumb: [
            { langkey: "home.breadcrumb", route: "home" },
            { langkey: "help_index.breadcrumb", route: "how-does-it-work" },
          ],
        },
      },
      {
        path: "/how-does-it-work/sign-up",
        name: "sign-up-help",
        component: HelpSignUp,
        meta: {
          scrollToTop: true,
          auth: false,
          breadcrumb: [
            { langkey: "home.breadcrumb", route: "home" },
            { langkey: "help_index.breadcrumb", route: "how-does-it-work" },
            { langkey: "sign_up_help.breadcrumb", route: "sign-up-help" },
          ],
        },
      },
      {
        path: "/how-does-it-work/account",
        name: "account-help",
        component: HelpAccount,
        meta: {
          scrollToTop: true,
          auth: false,
          breadcrumb: [
            { langkey: "home.breadcrumb", route: "home" },
            { langkey: "help_index.breadcrumb", route: "how-does-it-work" },
            { langkey: "account_help.breadcrumb", route: "account-help" },
          ],
        },
      },
      {
        path: "/how-does-it-work/purchases",
        name: "purchases-help",
        component: HelpPurchases,
        meta: {
          scrollToTop: true,
          auth: false,
          breadcrumb: [
            { langkey: "home.breadcrumb", route: "home" },
            { langkey: "help_index.breadcrumb", route: "how-does-it-work" },
            { langkey: "purchases_help.breadcrumb", route: "purchases-help" },
          ],
        },
      },
      {
        path: "/how-does-it-work/delivery",
        name: "delivery-help",
        component: HelpDelivery,
        meta: {
          scrollToTop: true,
          auth: false,
          breadcrumb: [
            { langkey: "home.breadcrumb", route: "home" },
            { langkey: "help_index.breadcrumb", route: "how-does-it-work" },
            { langkey: "delivery_help.breadcrumb", route: "delivery-help" },
          ],
        },
      },
      {
        path: "/how-does-it-work/payment",
        name: "payment-help",
        component: HelpPayment,
        meta: {
          scrollToTop: true,
          auth: false,
          breadcrumb: [
            { langkey: "home.breadcrumb", route: "home" },
            { langkey: "help_index.breadcrumb", route: "how-does-it-work" },
            { langkey: "payment_help.breadcrumb", route: "payment-help" },
          ],
        },
      },
      {
        path: "/customer-service",
        name: "customer-service",
        component: CustomerService,
        meta: {
          scrollToTop: true,
          auth: false,
          breadcrumb: [
            { langkey: "home.breadcrumb", route: "home" },
            {
              langkey: "customer_service.breadcrumb",
              route: "customer-service",
            },
          ],
        },
      },
      {
        path: "/pharmacies",
        name: "listpharmacies",
        component: PharmacyList,
        props: route => ({
          pharmacy: route.query.pharmacy,
          country: route.query.country,
        }),
        meta: {
          scrollToTop: true,
          auth: false,
        },
      },
      {
        path: "/login",
        name: "clinic-login",
        component: Login,
        meta: {
          scrollToTop: true,
          auth: false,
        },
      },
      {
        path: "/login",
        name: "medic-login",
        component: Login,
        meta: {
          scrollToTop: true,
          auth: false,
        },
      },
      {
        path: "/login",
        name: "insurance-login",
        component: Login,
        meta: {
          scrollToTop: true,
          auth: false,
        },
      },
      {
        path: "/legal",
        name: "legal",
        component: Legal,
        meta: {
          scrollToTop: true,
          auth: false,
        },
      },
      {
        path: "/terms",
        name: "terms_conditions",
        component: TermsAndConditions,
        meta: {
          scrollToTop: true,
          auth: false,
        },
      },
      {
        path: "/cookies",
        name: "cookies",
        component: Cookies,
        meta: {
          scrollToTop: true,
          auth: false,
        },
      },
      // en la web se están recibiendo peticiones hacia /privacidad, que no
      // existe. Probablemente por un link mal construido en alguna otra
      // web. Se gestiona aquí este link mal para darle validez y no
      // perder usuarios.
      // Además este link inexistente está lanzando errores en sentry por
      // lo que este redirect debería solucionarlo.
      // https://sentry.globalstorepharma.com/global-medika/globalstorepharma-com/issues/1779/events/c482530864ff4957986996f293b1bbaa/
      // https://sentry.globalstorepharma.com/global-medika/globalstorepharma-com/issues/1780/events/f4225042924c48d188a4d9c43189f00e/
      {
        path: "/privacidad",
        redirect: { name: "privacy" },
        name: "privacidad",
        meta: {
          auth: false,
        },
      },
      {
        path: "/privacy",
        name: "privacy",
        component: Privacy,
        meta: {
          scrollToTop: true,
          auth: false,
        },
      },
      {
        path: "/terms-of-sale",
        name: "terms_sale",
        component: TermsSale,
        meta: {
          scrollToTop: true,
          auth: false,
        },
      },
      {
        path: "/offer",
        redirect: { name: "product_list", params: { listType: "offers" } },
        name: "offers",
        meta: {
          auth: false,
        },
      },
      {
        path: "/categories/group",
        name: "grouped_categories",
        component: GroupedCategories,
        meta: {
          auth: false,
          scrollToTop: true,
          breadcrumb: [
            { langkey: "home.breadcrumb", route: "home" },
            { langkey: "categories.breadcrumb" },
          ],
        },
      },
      {
        path: "/categories/group/products",
        name: "grouped_categories_products",
        component: GroupedCategoriesProducts,
        meta: {
          auth: false,
          breadcrumb: [
            { langkey: "home.breadcrumb", route: "home" },
            { langkey: "categories.breadcrumb" },
          ],
        },
        props: route => ({
          categoryUuid: route.query.categoryUuid,
          groupedCategory: route.query.groupedCategory,
          brandUuid: route.query.brandUuid,
        }),
      },
      {
        path: "/offers-brand",
        redirect: {
          name: "product_list",
          params: { listType: "offers-brand" },
        },
        name: "brand_offers",
        meta: {
          scrollToTop: true,
          auth: false,
        },
      },
      {
        path: "/offers",
        redirect: { name: "product_list", params: { listType: "offers" } },
        name: "generic_offers",
        meta: {
          scrollToTop: true,
          auth: false,
        },
      },
      {
        path: "/offers-health",
        redirect: {
          name: "product_list",
          params: { listType: "offers-health" },
        },
        name: "health_offers",
        meta: {
          scrollToTop: true,
          auth: false,
        },
      },
      {
        path: "/offers-material",
        redirect: {
          name: "product_list",
          params: { listType: "offers-material" },
        },
        name: "equipment_offers",
        meta: {
          scrollToTop: true,
          auth: false,
        },
      },
      {
        path: "/category/:listType?",
        name: "product_list",
        component: ProductList,
        props: route => ({
          categoryUuid: route.query.categoryUuid,
          groupedCategoryUuid: route.query.groupedCategoryUuid,
          brandUuid: route.query.brandUuid,
        }),
        meta: {
          auth: false,
          myBreadcrumb: true,
        },
      },
      {
        name: "derma_category",
        path: "/derma",
        redirect:
          "/category/d51ac1a1-01e4-4cfb-92b3-6acfcfbc6426?categoryUuid=d51ac1a1-01e4-4cfb-92b3-6acfcfbc6426",
      },
      {
        path: "/onco",
        redirect:
          "/category/generic?categoryUuid=5de68dc3-22f4-4bcd-b5e7-27af0d57cd8a",
      },
      {
        path: "/product/:drugUuid",
        name: "product_sheet",
        component: ProductSheet,
        meta: {
          scrollToTop: true,
          auth: false,
        },
      },
      {
        path: "/product/:drugUuid/pharmacies",
        name: "product_pharmacies",
        component: ProductSheet,
        meta: {
          auth: false,
        },
      },
      {
        path: "/pharmacy/:pharmacyUuid",
        name: "pharmacy_sheet",
        component: PharmacyDetail,
        props: route => ({
          categoryUuid: route.query.categoryUuid,
          brandUuid: route.query.brandUuid,
        }),
        meta: {
          auth: false,
        },
      },
      {
        path: "/user/address/:pt_uuid/new",
        name: "useraddressnew",
        component: AddressForm,
        meta: {
          auth: true,
          breadcrumb: [
            { langkey: "home.breadcrumb", route: "home" },
            {
              langkey: "user_home.breadcrumb",
              route: "userhome",
              params: ["pt_uuid"],
            },
            {
              langkey: "user_address.breadcrumb",
              route: "useraddress",
              params: ["pt_uuid"],
            },
            { langkey: "user_address_new.breadcrumb" },
          ],
        },
      },
      {
        path: "/user/address/:pt_uuid/:aUuid",
        name: "useraddressedit",
        component: AddressForm,
        meta: {
          auth: true,
          breadcrumb: [
            { langkey: "home.breadcrumb", route: "home" },
            {
              langkey: "user_home.breadcrumb",
              route: "userhome",
              params: ["pt_uuid"],
            },
            {
              langkey: "user_address.breadcrumb",
              route: "useraddress",
              params: ["pt_uuid"],
            },
            { langkey: "user_address.breadcrumb" },
          ],
        },
      },
      {
        path: "/services",
        name: "services",
        component: Services,
        meta: {
          auth: false,
          /*breadcrumb: [
            { langkey: "home.breadcrumb", route: "home" },
            { langkey: "services.breadcrumb" },
            ],*/
        },
      },
      {
        path: "/services/import-medicines",
        name: "importmedicineshome",
        component: ImportMedicinesHome,
        meta: {
          auth: false,
        },
      },
      {
        path: "/services/import-medicines/form",
        name: "importmedicinesform",
        component: ImportMedicinesForm,
        meta: {
          auth: true,
        },
      },
      {
        path: "/services/import-medicines/done",
        name: "importmedicinesdone",
        component: ImportMedicinesDone,
        meta: {
          auth: true,
        },
      },
      {
        path: "/services/available-soon",
        name: "service-page",
        component: ServicesTemp,
        meta: {
          auth: false,
        },
      },
      {
        path: "/cart-payment-done",
        name: "cart-payment-done",
        component: ShoppingCartPaymentDoneIframe,
        meta: {
          auth: false,
        },
      },
      {
        path: "/cart-confirm/:checkout_uuid",
        name: "cart_confirm",
        component: TransactionCheckout,
        meta: {
          auth: true,
          scrollToTop: true,
          breadcrumb: [
            { langkey: "home.breadcrumb", route: "home" },
            {
              langkey: "cart_review.breadcrumb",
              route: "cart_review",
            },
            { langkey: "cart_confirm.breadcrumb" },
          ],
        },
      },
      {
        path: "/cart-review/:subaction?",
        name: "cart_review",
        component: ShoppingCartReview,
        meta: {
          auth: true,
          scrollToTop: true,
          breadcrumb: [
            { langkey: "home.breadcrumb", route: "home" },
            { langkey: "cart_review.breadcrumb" },
          ],
        },
      },
      {
        path: "/importationcart/checkout/:checkout_uuid",
        name: "importation_cart_checkout",
        component: TransactionCheckout,
        meta: {
          auth: true,
          scrollToTop: true,
        },
        props: {
          filter_address_by_current_country: true,
        },
      },
      {
        path: "/under-construction",
        name: "under_construction",
        component: Construction,
        meta: {
          auth: false,
        },
      },
      {
        path: "/not-found",
        name: "not_found",
        component: NotFound,
        meta: {
          auth: false,
        },
      },
      {
        path: "*",
        component: NotFound,
      },
    ],
    scrollBehavior,
  });

  router.beforeEach((to, from, next) => {
    const needAuth = to.matched.some(route => route.meta.auth);
    const breadcrumb = to.meta.breadcrumb;
    const myBreadcrumb = to.meta.myBreadcrumb;

    // TODO: commit instead dispatch?
    if (!myBreadcrumb) {
      store.dispatch("siteconf/CHANGE_BREADCRUMB", breadcrumb);
    }
    store.commit("routehelper/MUTATE_LAST_ROUTE", Object.assign({}, to));

    if (!needAuth) {
      next();
    } else {
      store.dispatch("auth/CHECK_TOKEN").then(
        () => {
          next();
        },
        () => {
          // No authenticado en una vista protegida. Redirección a Login
          // indicando la vista de origen para volver a esta tras logeo.
          next({ name: "login", params: { backto: to } });
        }
      );
    }
  });

  if (process.browser) {
    router.afterEach(to => {
      var _hsq = (window._hsq = window._hsq || []);
      _hsq.push(["setPath", to.fullPath]);
      _hsq.push(["trackPageView"]);

      var _ga = window.ga || function() {};
      _ga("set", "page", to.fullPath);
      _ga("send", "pageview");
    });
  }

  return router;
}
